import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { LanguageProvider } from "@/contexts/LanguageContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import HomePage from "@/pages/HomePage";
import SearchPage from "@/pages/SearchPage";
import PropertyDetailPage from "@/pages/PropertyDetailPage";
import AuthPage from "@/pages/AuthPage";
import DashboardPage from "@/pages/DashboardPage";
import SubmitPropertyPage from "@/pages/SubmitPropertyPage";
import AdminPage from "@/pages/AdminPage";
import TopAgentsPage from "@/pages/TopAgentsPage";
import PropertyVerificationPage from "@/pages/PropertyVerificationPage";
import AgencyDashboardPage from "@/pages/AgencyDashboardPage";
import LawyerDashboardPage from "@/pages/LawyerDashboardPage";
import BetaLandingPage from "@/pages/BetaLandingPage";
import BetaGate from "@/components/BetaGate";
import { BetaProvider } from "@/contexts/BetaContext";
import EditPropertyPage from "@/pages/EditPropertyPage";
import SuperAdminPage from "@/pages/SuperAdminPage";
import TransactionsListPage from "@/pages/TransactionsListPage";
import TransactionDashboardPage from "@/pages/TransactionDashboardPage";
import VerificationPage from "@/pages/VerificationPage";
import ChatbotConfigPage from "@/pages/ChatbotConfigPage";
import UpgradePage from "@/pages/UpgradePage";
import TourBookingPage from "@/pages/TourBookingPage";
import AgentToursPage from "@/pages/AgentToursPage";
import ProfilePage from "@/pages/ProfilePage";
import AgentProfilePage from "@/pages/AgentProfilePage";
import StatePage from "@/pages/StatePage";
import BulkUploadPage from "@/pages/BulkUploadPage";
import BecomeAgentPage from "@/pages/BecomeAgentPage";
import { PaymentCheckoutPage, MyPaymentsPage, AdminPaymentQueuePage, PaymentAdminConfigPage } from "@/pages/PaymentPages";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import ServiceAgreementPage from "@/pages/ServiceAgreementPage";
import ServiceAgreementGate from "@/components/ServiceAgreementGate";
import { FeedbackProvider, fireFeedbackTrigger } from "@/hooks/useFeedbackPrompts";
import FeedbackWidget, { FeedbackPromptCard } from "@/components/FeedbackWidget";
import AgentWelcomeModal from "@/components/AgentWelcomeModal";
import DataDeletionPage from "@/pages/DataDeletionPage";
import WhyVerifiedAgentsPage from "@/pages/WhyVerifiedAgentsPage";
import WhyVerifiedPropertiesPage from "@/pages/WhyVerifiedPropertiesPage";
import WhyTrackedTransactionsPage from "@/pages/WhyTrackedTransactionsPage";
import FirstVisitLanguageToast from "@/components/FirstVisitLanguageToast";
import { Skeleton } from "@/components/ui/skeleton";

function ProtectedRoute({ children, requiredRole }) {
  const { isAuthenticated, user, loading } = useAuth();
  if (loading) return <div className="flex items-center justify-center min-h-[50vh]"><Skeleton className="h-8 w-32" /></div>;
  if (!isAuthenticated) return <Navigate to="/auth" />;
  if (requiredRole === 'admin' && !['admin', 'superadmin'].includes(user?.role)) return <Navigate to="/" />;
  if (requiredRole === 'superadmin' && user?.role !== 'superadmin') return <Navigate to="/" />;
  return children;
}

function AppRoutes() {
  // Fire day_3_first_open when a logged-in user opens the app and their
  // account is between 3 and 14 days old — backend still enforces once-only.
  const { user } = useAuth();
  useEffect(() => {
    if (!user?.created_at) return;
    const created = new Date(user.created_at).getTime();
    if (Number.isNaN(created)) return;
    const days = (Date.now() - created) / 86400000;
    if (days >= 3 && days <= 14) {
      const id = setTimeout(() => fireFeedbackTrigger('day_3_first_open'), 3000);
      return () => clearTimeout(id);
    }
  }, [user?.id, user?.created_at]);

  return (
    <BetaGate>
      <Routes>
        <Route path="/beta" element={<BetaLandingPage />} />
        <Route path="*" element={<LayoutWithRoutes />} />
      </Routes>
    </BetaGate>
  );
}

function LayoutWithRoutes() {
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<ServiceAgreementPage />} />
        <Route path="/service-agreement" element={<ServiceAgreementPage />} />
        <Route path="/terms-legacy" element={<TermsPage />} />
        <Route path="/data-deletion" element={<DataDeletionPage />} />
        <Route path="/why/verified-agents" element={<WhyVerifiedAgentsPage />} />
        <Route path="/why/verified-properties" element={<WhyVerifiedPropertiesPage />} />
        <Route path="/why/tracked-transactions" element={<WhyTrackedTransactionsPage />} />
        <Route path="/search" element={<SearchPage />} />
        <Route path="/state/:key" element={<StatePage />} />
        <Route path="/property/:id" element={<PropertyDetailPage />} />
        <Route path="/property/:id/verify" element={<ProtectedRoute><PropertyVerificationPage /></ProtectedRoute>} />
        <Route path="/auth" element={<AuthPage />} />
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
        <Route path="/submit" element={<ProtectedRoute><SubmitPropertyPage /></ProtectedRoute>} />
        <Route path="/property/:id/edit" element={<ProtectedRoute><EditPropertyPage /></ProtectedRoute>} />
        <Route path="/admin" element={<ProtectedRoute requiredRole="admin"><AdminPage /></ProtectedRoute>} />
        <Route path="/admin/top-agents" element={<ProtectedRoute requiredRole="admin"><TopAgentsPage /></ProtectedRoute>} />
        <Route path="/superadmin" element={<ProtectedRoute requiredRole="superadmin"><SuperAdminPage /></ProtectedRoute>} />
        <Route path="/agency" element={<ProtectedRoute><AgencyDashboardPage /></ProtectedRoute>} />
        <Route path="/lawyer/dashboard" element={<ProtectedRoute><LawyerDashboardPage /></ProtectedRoute>} />
        <Route path="/agency/bulk-upload" element={<ProtectedRoute><BulkUploadPage /></ProtectedRoute>} />
        <Route path="/become-agent" element={<ProtectedRoute><BecomeAgentPage /></ProtectedRoute>} />
        <Route path="/payments" element={<ProtectedRoute><MyPaymentsPage /></ProtectedRoute>} />
        <Route path="/payments/checkout" element={<ProtectedRoute><PaymentCheckoutPage /></ProtectedRoute>} />
        <Route path="/admin/payments" element={<ProtectedRoute requiredRole="admin"><AdminPaymentQueuePage /></ProtectedRoute>} />
        <Route path="/admin/payment-config" element={<ProtectedRoute requiredRole="superadmin"><PaymentAdminConfigPage /></ProtectedRoute>} />
        <Route path="/transactions" element={<ProtectedRoute><TransactionsListPage /></ProtectedRoute>} />
        <Route path="/transaction/:id" element={<ProtectedRoute><TransactionDashboardPage /></ProtectedRoute>} />
        <Route path="/verification" element={<ProtectedRoute><VerificationPage /></ProtectedRoute>} />
        <Route path="/chatbot" element={<ProtectedRoute requiredRole="admin"><ChatbotConfigPage /></ProtectedRoute>} />
        <Route path="/upgrade" element={<ProtectedRoute><UpgradePage /></ProtectedRoute>} />
        <Route path="/book-tour/:propertyId/:agentId" element={<ProtectedRoute><TourBookingPage /></ProtectedRoute>} />
        <Route path="/agent/tours" element={<ProtectedRoute><AgentToursPage /></ProtectedRoute>} />
        <Route path="/profile" element={<ProtectedRoute><ProfilePage /></ProtectedRoute>} />
        <Route path="/agent/:id" element={<AgentProfilePage />} />
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <BrowserRouter>
      <LanguageProvider>
        <AuthProvider>
          <BetaProvider>
            <FeedbackProvider>
              <ServiceAgreementGate>
                <AppRoutes />
              </ServiceAgreementGate>
              <FeedbackWidget />
              <FeedbackPromptCard />
              <AgentWelcomeModal />
              <FirstVisitLanguageToast />
              <Toaster position="top-right" richColors />
            </FeedbackProvider>
          </BetaProvider>
        </AuthProvider>
      </LanguageProvider>
    </BrowserRouter>
  );
}

export default App;
